import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MeetingsStatisticsAPI from '../../../api/MeetingsStatisticsAPI';
import LinearGraph from '../../../components/LinearGraph/LinearGraph';
import NoData from '../../../components/NoData/NoData';
import { bucketizeDailySeriesForChart } from '../../../shared/bucketizeDailySeriesForChart';

type Props = {
  timeRange: { start: string; end: string };
};

function MeetingsTotalOverTime({ timeRange }: Props) {
  const { t, i18n } = useTranslation();
  const [dailyLabels, setDailyLabels] = useState<string[]>([]);
  const [dailyValues, setDailyValues] = useState<number[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await MeetingsStatisticsAPI.getMeetingsTotalOverTime({ startTime: timeRange.start, endTime: timeRange.end });
        setDailyLabels(res.data?.labels || []);
        setDailyValues(res.data?.values || []);
      } catch {
        setDailyLabels([]);
        setDailyValues([]);
      }
    };
    fetchData();
  }, [timeRange.start, timeRange.end]);

  const { xLabels, values, total } = useMemo(() => {
    const rangeStart = new Date(timeRange.start);
    const rangeEnd = new Date(timeRange.end);
    const { xLabels: xl, values: vals } = bucketizeDailySeriesForChart(
      rangeStart,
      rangeEnd,
      dailyLabels,
      dailyValues,
      i18n.language,
    );
    const sum = vals.reduce((a, b) => a + b, 0);
    return { xLabels: xl, values: vals, total: sum };
  }, [timeRange.start, timeRange.end, dailyLabels, dailyValues, i18n.language]);

  if (total === 0) return <NoData message={t('global.empty_results')} />;

  return (
    <LinearGraph
      title={t('stats.meetings_total_over_time') || 'Meetings over time'}
      xLabels={xLabels}
      showAllXTicks
      dataSets={[
        {
          label: t('stats.meetings') || 'Meetings',
          data: values,
          borderColor: 'rgba(99, 102, 241, 0.9)',
          backgroundColor: 'rgba(99, 102, 241, 0.2)',
          borderWidth: 2,
        },
      ]}
    />
  );
}

export default MeetingsTotalOverTime;
